"use client";

import Link from "next/link";
import { ArrowRight, Crosshair, Lock, Zap, Plus } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { values as data } from "@/lib/data/about";

const glyphMap = {
  crosshair: Crosshair,
  lock: Lock,
  bolt: Zap,
} as const;
const accentMap = {
  crimson: "var(--color-crimson)",
  purple: "var(--color-purple)",
  cyan: "var(--color-cyan)",
  emerald: "var(--color-emerald)",
  amber: "var(--color-amber)",
} as const;

export function Values() {
  return (
    <Section id="values" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute bottom-1/4 right-1/4 h-80 w-80 rounded-full bg-[var(--color-amber)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-amber)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {data.values.map((v, i) => {
            const Glyph = glyphMap[v.glyph as keyof typeof glyphMap] ?? Crosshair;
            const accent = accentMap[v.accent];
            return (
              <Reveal key={v.title} delay={i * 100}>
                <div
                  className="relative h-full rounded-3xl glass p-7 overflow-hidden"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)`,
                  }}
                >
                  <div
                    className="pointer-events-none absolute -bottom-24 -right-24 h-56 w-56 rounded-full opacity-20 blur-3xl"
                    style={{ background: accent }}
                  />
                  <div className="relative">
                    <div className="flex items-center gap-3">
                      <span
                        className="grid place-items-center h-12 w-12 rounded-2xl"
                        style={{
                          background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                        }}
                      >
                        <Glyph className="h-5 w-5" style={{ color: accent }} />
                      </span>
                      <span className="eyebrow-tiny" style={{ color: accent }}>
                        Value {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="mt-5">{v.title}</h3>
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wider" style={{ color: accent }}>
                      {v.principle}
                    </p>
                    <p className="mt-3 text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed">{v.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 glass rounded-2xl p-7 flex items-start gap-4">
            <span
              className="grid place-items-center h-10 w-10 rounded-xl shrink-0"
              style={{
                background: "color-mix(in oklch, var(--color-cyan) 18%, transparent)",
                boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-cyan) 35%, transparent)",
              }}
            >
              <Plus className="h-4 w-4 text-[var(--color-cyan)]" />
            </span>
            <div>
              <h3 className="text-base font-bold">{data.footnote.heading}</h3>
              <p className="mt-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{data.footnote.body}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
